import { NgClass, isPlatformBrowser } from '@angular/common'
import type { Signal } from '@angular/core'
import {
  Component,
  PLATFORM_ID,
  computed,
  inject,
  input,
  output,
} from '@angular/core'
import { Router, RouterLink, RouterLinkActive, isActive } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'

import { AuthService } from '../../../../services/auth/auth.service'
import type { DialogState } from '../../../../services/dialog/dialog.service'
import { DialogService } from '../../../../services/dialog/dialog.service'
import type { NavItem } from '../../interfaces/nav-item.interface'
import { ProfileIconComponent } from '../profile-icon/profile-icon.component'

@Component({
  selector: 'dhp-mobile-menu',
  templateUrl: './mobile-menu.component.html',
  imports: [
    NgClass,
    RouterLink,
    TranslatePipe,
    ProfileIconComponent,
    RouterLinkActive,
  ],
})
export class MobileMenuComponent {
  private router = inject(Router)
  private platformId = inject(PLATFORM_ID)
  dialogService = inject(DialogService)
  private authService = inject(AuthService)
  private readonly routeActiveSignals = new Map<string, Signal<boolean>>()

  readonly navItems = input<NavItem[]>([])
  readonly menuClose = output()
  readonly searchStateChange = output<boolean>()
  currentUser = computed(() => this.authService.currentUser())

  toggleDropdown(dropdownKey: keyof DialogState, event?: MouseEvent): void {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    this.dialogService.toggleDropdown(dropdownKey)
  }

  closeMenu(): void {
    this.dialogService.toggle('menu', false)
    // TODO: The 'emit' function requires a mandatory void argument
    this.menuClose.emit()
  }

  changeLanguage(): void {
    this.dialogService.toggle('language', true)
  }

  toggleSearch(): void {
    this.closeMenu()
    void this.router.navigate(['/'])
  }

  async logout(): Promise<void> {
    await this.authService.logout()
    this.closeMenu()
    await this.router.navigate(['/'])
  }

  handleDropdownItemClick(item: NavItem): void {
    if (item.dropdownItems && item.dropdown) {
      this.dialogService.toggle(item.dropdown, false)
    }
    this.closeMenu()
  }

  isRouteActive(url: string | undefined): boolean {
    if (!url) return false
    const cleanUrl = url.startsWith('/') ? url : `/${url}`
    return this.getRouteActiveSignal(cleanUrl)()
  }

  private getRouteActiveSignal(url: string): Signal<boolean> {
    let activeSignal = this.routeActiveSignals.get(url)
    if (!activeSignal) {
      activeSignal = isActive(url, this.router, {
        paths: 'exact',
        queryParams: 'exact',
        fragment: 'ignored',
        matrixParams: 'ignored',
      })
      this.routeActiveSignals.set(url, activeSignal)
    }

    return activeSignal
  }

  isAnyChildActive(item: NavItem): boolean {
    if (!item.dropdownItems) return false
    return item.dropdownItems.some(child => this.isRouteActive(child.url))
  }

  get isAuthenticated(): boolean {
    return this.authService.isAuthenticated()
  }

  get userName(): string | null {
    return this.authService.currentUser()?.name ?? null
  }

  get organization(): string | null {
    return this.authService.currentUser()?.organization?.name ?? null
  }

  get returnUrl(): string {
    if (isPlatformBrowser(this.platformId)) {
      return window.location.pathname
    }

    return this.router.url || '/'
  }
}
